import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { ErrorBoundary } from "../../../components/ErrorBoundary";
import { LazyIcon } from "../../../components/LazyIcon";
import { TripWithTenant } from "../../../types";
import { SplitButton } from "../../../widgets/SplitButton/SplitButton";
import { CreateTripDialog } from "../CreateTripDialog/CreateTripDialog";
import { CurrentTimeControl } from "../CurrentTimeControl/CurrentTimeControl";
import { LocationFilter } from "../LocationFilter";
import { ScheduleList } from "../ScheduleList/ScheduleList";
import { ScheduleTimeline } from "../ScheduleTimeline/ScheduleTimeline";
import { SimpleBulkUpload } from "../SimpleBulkUpload/SimpleBulkUpload";
import { TripDetailsModal } from "../TripDetailsModal/TripDetailsModal";

interface ScheduleDashboardPresenterProps {
  // FBO state
  activeFBO: any;

  // Current time control
  currentTime: Date;
  handleTimeChange: (time: Date) => void;
  handleNowClick: () => void;
  isTimelineScrolledAway: boolean;

  // Date range
  startDate: Date;
  endDate: Date;

  // Trips data
  trips: TripWithTenant[];
  filteredTrips: TripWithTenant[];
  loading: boolean;
  error: string | null;

  // Dialog states
  createDialogOpen: boolean;
  setCreateDialogOpen: (open: boolean) => void;
  bulkUploadOpen: boolean;
  setBulkUploadOpen: (open: boolean) => void;
  tripDetailsOpen: boolean;
  selectedTrip: TripWithTenant | null;

  // View mode
  viewMode: "table" | "timeline";
  handleViewModeChange: (
    event: React.MouseEvent<HTMLElement>,
    newMode: "table" | "timeline" | null
  ) => void;

  // Actions
  loadTrips: () => Promise<void>;
  handleTripCreated: () => void;
  handleTripEdit: (trip: TripWithTenant) => void;
  handleTripDetailsClose: () => void;
  handleBulkUploadComplete: (count: number) => void;
  handleTimeRangeChange: (startDate: Date, endDate: Date) => void;

  // Scroll state
  shouldResetScroll: boolean;
  handleScrollReset: () => void;
  handleTimelineScrollChange: (scrolledAway: boolean) => void;

  // Filters
  locations: Array<{
    id: string;
    name: string;
    type: "hangar" | "ramp";
    tripCount: number;
  }>;
  selectedLocationIds: string[];
  setSelectedLocationIds: (locationIds: string[]) => void;
  statusFilter: string;
  setStatusFilter: (filter: string) => void;
  tailNumberFilter: string;
  setTailNumberFilter: (filter: string) => void;
}

export const ScheduleDashboardPresenter: React.FC<ScheduleDashboardPresenterProps> = ({
  activeFBO,
  currentTime,
  handleTimeChange,
  handleNowClick,
  isTimelineScrolledAway,
  startDate,
  endDate,
  trips,
  filteredTrips,
  loading,
  error,
  createDialogOpen,
  setCreateDialogOpen,
  bulkUploadOpen,
  setBulkUploadOpen,
  tripDetailsOpen,
  selectedTrip,
  viewMode,
  handleViewModeChange,
  loadTrips,
  handleTripCreated,
  handleTripEdit,
  handleTripDetailsClose,
  handleBulkUploadComplete,
  handleTimeRangeChange,
  shouldResetScroll,
  handleScrollReset,
  handleTimelineScrollChange,
  locations,
  selectedLocationIds,
  setSelectedLocationIds,
  statusFilter,
  setStatusFilter,
  tailNumberFilter,
  setTailNumberFilter,
}) => {
  if (!activeFBO) {
    return (
      <Container>
        <Alert severity="warning">
          Please select an FBO to view the schedule.
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ height: "100%", py: 3, px: 3 }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="h4">Trip Schedule</Typography>
            {selectedLocationIds.length > 0 && (
              <Chip
                label={`Filtered: ${selectedLocationIds.length} location${selectedLocationIds.length > 1 ? 's' : ''}`}
                size="small"
                color="primary"
                variant="outlined"
                onDelete={() => setSelectedLocationIds([])}
              />
            )}
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
              sx={{
                "& .MuiToggleButton-root": {
                  px: 2,
                  py: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  "&.Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                },
              }}
            >
              <Tooltip title="View trips in a visual timeline/GANTT chart">
                <ToggleButton value="timeline" aria-label="timeline view">
                  <LazyIcon name="TimelineIcon" sx={{ mr: 0.5 }} />
                  Timeline
                </ToggleButton>
              </Tooltip>
              <Tooltip title="View trips in a detailed data table">
                <ToggleButton value="table" aria-label="table view">
                  <LazyIcon name="TableViewIcon" sx={{ mr: 0.5 }} />
                  Table
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
            <SplitButton
              startIcon={<LazyIcon name="AddIcon" />}
              options={[
                {
                  label: "Create Trip",
                  onClick: () => setCreateDialogOpen(true),
                },
                {
                  label: "Bulk Import",
                  onClick: () => setBulkUploadOpen(true),
                },
              ]}
            />
          </Stack>
        </Stack>

        {/* Filters and Controls */}
        <Paper
          elevation={1}
          sx={{
            p: 2,
            mb: 2,
            backgroundColor: "background.paper",
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            {/* Left side: Current Time Control */}
            <CurrentTimeControl
              currentTime={currentTime}
              startDate={startDate}
              endDate={endDate}
              onTimeChange={handleTimeChange}
              onNowClick={handleNowClick}
              isScrolledAway={isTimelineScrolledAway}
            />

            {/* Right side: All filters grouped together */}
            <Stack direction="row" spacing={1} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="scheduled">Scheduled</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Filter by Tail Number"
                variant="outlined"
                size="small"
                value={tailNumberFilter}
                onChange={(e) => setTailNumberFilter(e.target.value)}
                sx={{ minWidth: 200 }}
                placeholder="N123AB"
              />

              <LocationFilter
                locations={locations}
                selectedLocationIds={selectedLocationIds}
                onChange={setSelectedLocationIds}
                disabled={loading}
              />
            </Stack>
          </Stack>
        </Paper>

        {/* Content */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : viewMode === "timeline" ? (
          <>
            {/* Timeline View - Always show, even with no trips */}
            <ErrorBoundary
              fallbackTitle="Timeline Error"
              fallbackDescription="There was an error loading the timeline view. Please try refreshing or switching to table view."
            >
              <ScheduleTimeline
                trips={filteredTrips}
                onTripsChanged={loadTrips}
                startDate={startDate}
                endDate={endDate}
                viewTimestamp={currentTime}
                onTimeRangeChange={handleTimeRangeChange}
                shouldResetScroll={shouldResetScroll}
                onScrollReset={handleScrollReset}
                onScrollChange={handleTimelineScrollChange}
              />
            </ErrorBoundary>
          </>
        ) : (
          <>
            {/* Table View - Show empty state for tables */}
            {filteredTrips.length === 0 ? (
              <Paper elevation={1} sx={{ p: 4, textAlign: "center" }}>
                <Typography>
                  No trips scheduled for the selected date range
                </Typography>
              </Paper>
            ) : (
              <ScheduleList
                trips={filteredTrips}
                loading={loading}
                onTripsUpdated={loadTrips}
                onTripEdit={handleTripEdit}
                startDate={startDate}
                endDate={endDate}
                statusFilter={statusFilter}
                tailNumberFilter={tailNumberFilter}
              />
            )}
          </>
        )}

        {/* Create Trip Dialog */}
        <CreateTripDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onSuccess={handleTripCreated}
        />

        {/* Trip Details Modal */}
        <TripDetailsModal
          open={tripDetailsOpen}
          trip={selectedTrip}
          trips={trips}
          onClose={handleTripDetailsClose}
          onUpdate={undefined}
          onTripChanged={loadTrips}
        />

        {/* Bulk Upload Dialog */}
        <SimpleBulkUpload
          open={bulkUploadOpen}
          onClose={() => setBulkUploadOpen(false)}
          onComplete={handleBulkUploadComplete}
        />
      </Stack>
    </Box>
  );
};